"use client"

import { useState } from "react"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../components/ui/dialog"
import { Button } from "../../components/ui/button"
import { Input } from "../../components/ui/input"
import { Label } from "../../components/ui/label"
import { CheckCircle, Loader2, Sparkles } from "lucide-react"
import { useTheme } from "./theme-provider"
import emailjs from '@emailjs/browser'
import logger from '../../utils/logger'

// Initialize EmailJS
emailjs.init("XVF9Hun7gv3qgTRUJ") // Replace with your actual public key

const EarlyAccessModal = ({ isOpen, setIsOpen, selectedPlan = null }) => {
  const [email, setEmail] = useState("")
  const [name, setName] = useState("")
  const [website, setWebsite] = useState("")
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [error, setError] = useState("")
  const { theme } = useTheme();
  const isDarkMode = theme === "dark";

  const handleSubmit = (e) => {
    e.preventDefault()
    setError("")

    if (!email) {
      setError("Please enter your email address")
      return
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError("Please enter a valid email address")
      return
    }

    setIsSubmitting(true)

    // Get current date
    const currentDate = new Date().toLocaleString()

    // Debug: Log the data being sent to EmailJS
    const templateParams = {
      name: name || "Not provided",
      email: email,
      website: website || "Not provided",
      plan: selectedPlan ? selectedPlan.name : "No plan selected",
      date: currentDate
    }
    
    logger.log("Sending to EmailJS:", templateParams)
    logger.log("Selected Plan Object:", selectedPlan)

    // Send email using EmailJS
    emailjs.send(
      'service_ninyd0i', // Your EmailJS service ID
      'template_51biw6k', // Your EmailJS template ID
      templateParams
    )
    .then((response) => {
      logger.log("EmailJS SUCCESS:", response)
      setIsSubmitting(false)
      setIsSubmitted(true)

      // Reset form after 3 seconds and close modal
      setTimeout(() => {
        setEmail("")
        setName("")
        setWebsite("")
        setIsSubmitted(false)
        setIsOpen(false)
      }, 3000)
    })
    .catch((error) => {
      logger.error("EmailJS error:", error)
      setError("Failed to submit. Please try again later.")
      setIsSubmitting(false)
    })
  }

  // Get title and description based on context
  const getTitle = () => {
    if (!selectedPlan) return "Get Free Early Access"
    return `Get Free Early Access to ${selectedPlan.name}`
  }

  const getDescription = () => {
    if (!selectedPlan) return "Join our exclusive early access program and be the first to experience SmushLabs.ai."
    return `You've selected the ${selectedPlan.name} plan. Join our exclusive early access program to be among the first to experience this plan when we launch.`
  }

  const getPlanIcon = () => {
    if (!selectedPlan) return (
      <div className={`inline-flex items-center justify-center w-12 h-12 rounded-full ${
        isDarkMode 
          ? 'bg-gradient-to-br from-purple-600/80 to-blue-600/80'
          : 'bg-gradient-to-br from-purple-500/20 to-blue-500/30'
      } mb-4`}>
        <Sparkles className={`h-6 w-6 ${isDarkMode ? 'text-white' : 'text-primary'}`} />
      </div>
    )

    const bgGradient = isDarkMode
      ? selectedPlan.darkColor || selectedPlan.color
      : selectedPlan.color;

    return (
      <div
        className={`inline-flex items-center justify-center w-12 h-12 rounded-full bg-gradient-to-br ${bgGradient} mb-4 ${
          isDarkMode ? 'shadow-lg shadow-purple-900/20' : ''
        }`}
      >
        {selectedPlan.icon}
      </div>
    )
  }

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent className={`sm:max-w-md ${
        isDarkMode 
          ? 'bg-gradient-to-br from-gray-900 to-gray-950 border-gray-800 shadow-xl shadow-purple-900/10'
          : ''
      }`}>
        {isDarkMode && (
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute -top-24 -right-24 w-64 h-64 bg-purple-600/5 rounded-full blur-3xl"></div>
            <div className="absolute -bottom-24 -left-24 w-64 h-64 bg-blue-600/5 rounded-full blur-3xl"></div>
          </div>
        )}
        
        <DialogHeader className="relative z-10">
          <div className="flex flex-col items-center text-center">
            {getPlanIcon()}
            <DialogTitle className={`text-2xl ${isDarkMode ? 'text-white' : ''}`}>{getTitle()}</DialogTitle>
            <DialogDescription className={isDarkMode ? 'text-gray-300' : ''}>{getDescription()}</DialogDescription>
          </div>
        </DialogHeader>

        <div className="relative z-10">
          {!isSubmitted ? (
            <form onSubmit={handleSubmit} className="space-y-4 pt-4">
              <div className="space-y-2">
                <Label htmlFor="name" className={isDarkMode ? 'text-gray-200' : ''}>Name</Label>
                <Input 
                  id="name" 
                  placeholder="Your name" 
                  value={name} 
                  onChange={(e) => setName(e.target.value)} 
                  className={isDarkMode ? 'bg-gray-800/50 border-gray-700 focus:border-purple-500 text-white' : ''}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="website" className={isDarkMode ? 'text-gray-200' : ''}>Website URL</Label>
                <Input 
                  id="website" 
                  placeholder="https://yourcompany.com" 
                  value={website} 
                  onChange={(e) => setWebsite(e.target.value)} 
                  className={isDarkMode ? 'bg-gray-800/50 border-gray-700 focus:border-purple-500 text-white' : ''}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email" className={isDarkMode ? 'text-gray-200' : ''}>
                  Email <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className={isDarkMode ? 'bg-gray-800/50 border-gray-700 focus:border-purple-500 text-white' : ''}
                />
              </div>

              {error && <p className="text-red-500 text-sm">{error}</p>}

              <Button
                type="submit"
                className={`w-full ${
                  isDarkMode 
                    ? selectedPlan && selectedPlan.special
                      ? "bg-gradient-to-r from-amber-600 to-rose-600 hover:from-amber-700 hover:to-rose-700"
                      : "bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700"
                    : selectedPlan && selectedPlan.special
                      ? "bg-gradient-to-r from-amber-500 to-red-500 hover:from-amber-600 hover:to-red-600"
                      : ""
                }`}
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Submitting...
                  </>
                ) : selectedPlan ? (
                  selectedPlan.cta
                ) : (
                  "Join the Waitlist"
                )}
              </Button>

              <p className={`text-xs text-center ${isDarkMode ? 'text-gray-400' : 'text-foreground/60'}`}>
                You will recieve an Email with instructions to avail the free early access.
              </p>
            </form>
          ) : (
            <div className={`py-8 text-center ${isDarkMode ? 'bg-gray-800/30 rounded-xl p-6 backdrop-blur-sm' : ''}`}>
              <div className="flex justify-center mb-4">
                <div className={isDarkMode ? 'p-3 bg-green-500/10 rounded-full' : ''}>
                  <CheckCircle className={`h-12 w-12 ${isDarkMode ? 'text-green-400' : 'text-green-500'}`} />
                </div>
              </div>
              <h3 className={`text-xl font-medium mb-2 ${isDarkMode ? 'text-white' : ''}`}>Thank You!</h3>
              <p className={isDarkMode ? 'text-gray-300' : 'text-foreground/70'}>
                {selectedPlan
                  ? `You've been added to our early access list for the ${selectedPlan.name} plan. We'll notify you when SmushLabs.ai launches.`
                  : "You've been added to our early access list. We'll notify you when SmushLabs.ai launches."}
              </p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default EarlyAccessModal